import React, { useState } from 'react';
import moment from 'moment';
import { FormInput } from 'components/form';
import { Circle } from '@mui/icons-material';
import { DateFormats } from 'constants/common';
import { blueGrey, grey } from '@mui/material/colors';
import { useOptimisticState } from 'hooks/useOptimistic';
import { BpfLinkType } from '@trnsact/trnsact-shared-types';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';

import { StageNote } from '../../types';
import { useBPFStages } from '../../api';

interface Props {
  parentId: string;
  stageId: string;
  loading: boolean;
  apiNotes: StageNote[];
  parentType: BpfLinkType;
}

const SYNTHETIC_ID = '-100' as const;

export const StageNotes = ({ loading, apiNotes, stageId, parentType, parentId }: Props) => {
  const { isNoteCreating, handleCreateStageNote } = useBPFStages();

  const [notes, setNotes] = useOptimisticState(apiNotes);
  const [newNote, setNewNote] = useState<string>('');

  const onAddStageNote = () => {
    const note: StageNote = {
      id: SYNTHETIC_ID,
      stageId,
      parentId,
      parentType,
      createdById: '',
      createdBy: null,
      content: newNote,
      modifiedDateTime: null,
      createdDateTime: new Date().toISOString(),
    };

    setNotes(
      handleCreateStageNote({ content: newNote, stageId, parentId, parentType }).then(value =>
        value ? [value, ...notes] : notes
      ),
      [note, ...notes]
    );

    setNewNote('');
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" gap="0.5rem">
          <Typography variant="subtitle2" component="span">
            Notes
          </Typography>

          <RenderWithCondition condition={loading}>
            <CircularProgress size={16} />
          </RenderWithCondition>
        </Stack>

        <Button loading={isNoteCreating} disabled={!newNote} onClick={onAddStageNote}>
          Save
        </Button>
      </Stack>

      <Stack gap="0.5rem" overflow="auto">
        <FormInput
          multiline
          minRows={4}
          maxRows={4}
          value={newNote}
          variant="outlined"
          placeholder="Enter note"
          onChange={value => setNewNote(value)}
        />

        <Stack gap="0.5rem">
          {notes.map(note => (
            <Box
              key={note.id}
              sx={{
                borderRadius: '0.5rem',
                padding: '0.5rem 0.75rem',
                border: `1px solid ${blueGrey[100]}`,
                backgroundColor: isNoteCreating && note.id === SYNTHETIC_ID ? grey[200] : grey[50],
              }}
            >
              <Stack direction="row" alignItems="center" gap="0.5rem">
                <Typography variant="subtitle2" color="textSecondary" component="span">
                  {note.createdBy?.fullName ?? 'Processing...'}
                </Typography>

                <Circle color="disabled" sx={{ fontSize: '6px' }} />

                <Typography variant="subtitle2" color="textSecondary">
                  {moment(note.createdDateTime).format(DateFormats.DateTimeShort24Hour)}
                </Typography>
              </Stack>

              <Typography variant="body2">{note.content}</Typography>
            </Box>
          ))}
        </Stack>
      </Stack>
    </Box>
  );
};
